import { isNil } from 'lodash';

/**
 * Determines whether a geographic coordinate lies within the specified bounds.
 *
 * @param longitude - The longitude of the coordinate.
 * @param latitude - The latitude of the coordinate.
 * @param bounds - The geographic bounds in the format `[minLongitude, minLatitude, maxLongitude, maxLatitude]`.
 * @returns `false` when bounds are not provided.
 * @returns `true` if the coordinate lies within the bounds (inclusive);
 * otherwise, `false`.
 */
export const isCoordinateWithinBounds = (
  longitude: number,
  latitude: number,
  bounds?: [number, number, number, number] | null,
): boolean => {
  if (isNil(bounds)) {
    return false;
  }

  const [minLongitude, minLatitude, maxLongitude, maxLatitude] = bounds;

  return (
    longitude >= minLongitude &&
    longitude <= maxLongitude &&
    latitude >= minLatitude &&
    latitude <= maxLatitude
  );
};

/**
 * Converts geographic coordinates (longitude, latitude) to Decimal Degrees (DD).
 *
 * @param {number} longitude - The longitude value of the point.
 * @param {number} latitude - The latitude value of the point.
 * @returns {[string, string]} An array containing the formatted longitude and latitude values.
 */
export const getDDValues = (
  longitude: number,
  latitude: number,
): [string, string] => {
  return [longitude.toFixed(7), latitude.toFixed(7)];
};

/**
 * Converts geographic coordinates (longitude, latitude) to Degrees Minutes Seconds (DMS).
 *
 * @param {number} coordinateValue - The coordinate value to convert.
 * @param {boolean} [isLatitude=false] - Whether the coordinate is a latitude.
 * @returns {string} The formatted coordinate value in DMS format.
 */
export const getDMSValues = (
  coordinateValue: number,
  isLatitude = false,
): string => {
  let direction: string;

  if (isLatitude) {
    direction = coordinateValue >= 0 ? 'N' : 'S';
  } else {
    direction = coordinateValue >= 0 ? 'E' : 'W';
  }
  const absoluteValue = Math.abs(coordinateValue);
  const degrees = Math.floor(absoluteValue);
  const minutesFloat = (absoluteValue - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = ((minutesFloat - minutes) * 60).toFixed(2);
  return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
};
